/* =========================================================
   src/ui.jsx - shadcn-flavored primitives (Button, Input,
   Textarea, Label, Field, Alert)
   ========================================================= */

import React from "react";

const cx = (...parts) => parts.filter(Boolean).join(" ");

/* --- Button ---------------------------------------------------------- */
function Button({
  variant = "primary",
  size = "md",
  asChild,
  className,
  children,
  ...props
}) {
  const cls = cx(
    "btn",
    variant === "primary" && "btn-primary",
    variant === "secondary" && "btn-secondary",
    variant === "ghost" && "btn-ghost",
    size === "lg" && "btn-lg",
    size === "sm" && "btn-sm",
    size === "icon" && "btn-icon",
    className
  );
  if (asChild) {
    return React.cloneElement(React.Children.only(children), {
      className: cx(cls, children.props.className),
      ...props,
    });
  }
  return (
    <button className={cls} {...props}>
      {children}
    </button>
  );
}

/* --- Label ------------------------------------------------------- */
function Label({ htmlFor, children, hint, className, ...props }) {
  return (
    <label htmlFor={htmlFor} className={cx("label", className)} {...props}>
      {children}
      {hint && <span className="label-hint">{hint}</span>}
    </label>
  );
}

/* --- Input ------------------------------------------------------- */
const Input = React.forwardRef(function Input(
  { className, type = "text", ...props }, ref
) {
  return (
    <input ref={ref} type={type} className={cx("input", className)} {...props} />
  );
});

/* --- Textarea ---------------------------------------------------- */
const Textarea = React.forwardRef(function Textarea(
  { className, ...props }, ref
) {
  return (
    <textarea ref={ref} className={cx("textarea", className)} {...props} />
  );
});

/* --- Field (wrapper: label + control + error) -------------------- */
function Field({ id, label, hint, error, children, className }) {
  return (
    <div className={cx("field", error && "field-error", className)}>
      {label && <Label htmlFor={id} hint={hint}>{label}</Label>}
      {children}
      {error && <div className="field-error-msg">{error}</div>}
    </div>
  );
}

/* --- Alert ------------------------------------------------------- */
function Alert({ variant = "success", icon, children }) {
  return (
    <div className={cx("alert", variant === "success" && "alert-success")}>
      {icon || <Icon name="CheckCircle2" size={18} />}
      <div>{children}</div>
    </div>
  );
}

Object.assign(window, { cx, Button, Label, Input, Textarea, Field, Alert });
